import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Check, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../../api/client';
import type { ChecklistItem, ChecklistTemplate, Event } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { ProgressBar } from '../../../components/ui/ProgressBar';
import { Modal } from '../../../components/ui/Modal';

const CATEGORIES = ['Pre-evento', 'Giorno evento', 'Post-produzione', 'Consegna'];

export default function ChecklistTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const [newItem, setNewItem] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [applyMode, setApplyMode] = useState<'append' | 'replace'>('append');

  const { data: items = [] } = useQuery<ChecklistItem[]>({
    queryKey: ['checklist', event.id],
    queryFn: () => apiGet<ChecklistItem[]>(`/events/${event.id}/checklist`),
  });

  const { data: templates = [] } = useQuery<ChecklistTemplate[]>({
    queryKey: ['checklist-templates', event.eventType],
    queryFn: () => apiGet<ChecklistTemplate[]>(`/checklist-templates?eventType=${event.eventType}`),
    enabled: showTemplateModal,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['checklist', event.id] });

  const addMutation = useMutation({
    mutationFn: ({ label, category }: { label: string; category: string }) =>
      apiPost(`/events/${event.id}/checklist`, { label, category }),
    onSuccess: () => { invalidate(); setNewItem(''); toast.success('Item aggiunto'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ itemId, completed }: { itemId: string; completed: boolean }) =>
      apiPatch(`/events/${event.id}/checklist/${itemId}`, { completed }),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => apiDelete(`/events/${event.id}/checklist/${itemId}`),
    onSuccess: () => { invalidate(); toast.success('Item eliminato'); },
  });

  const applyMutation = useMutation({
    mutationFn: ({ templateId, mode }: { templateId: string; mode: 'append' | 'replace' }) =>
      apiPost(`/events/${event.id}/checklist/apply-template`, { templateId, mode }),
    onSuccess: () => {
      invalidate();
      setShowTemplateModal(false);
      toast.success('Template applicato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const grouped = CATEGORIES.reduce<Record<string, ChecklistItem[]>>((acc, cat) => {
    acc[cat] = items.filter(i => i.category === cat || (!i.category && cat === CATEGORIES[0]));
    return acc;
  }, {});

  const total = items.length;
  const done = items.filter(i => i.completed).length;
  const pct = total > 0 ? (done / total) * 100 : 0;

  const defaultTemplate = templates.find(t => t.isDefault);

  const handleOpenModal = () => {
    setSelectedTemplateId('');
    setApplyMode(items.length === 0 ? 'replace' : 'append');
    setShowTemplateModal(true);
  };

  const handleApply = () => {
    const id = selectedTemplateId || defaultTemplate?.id;
    if (!id) return;
    applyMutation.mutate({ templateId: id, mode: applyMode });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{ fontWeight: 600 }}>Completamento: {done}/{total}</p>
          <Button size="sm" variant="secondary" onClick={handleOpenModal} icon={<LayoutList size={14} />}>
            Applica template
          </Button>
        </div>
        <ProgressBar value={pct} showLabel />
      </div>

      {/* Empty state banner */}
      {items.length === 0 && (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px dashed rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          padding: '1.5rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          <LayoutList size={32} color="var(--color-text-faint)" />
          <div>
            <p style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Nessun item nella checklist</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {defaultTemplate
                ? `Applica il template predefinito per ${event.eventType.toLowerCase()} (${defaultTemplate.items.length} item) oppure aggiungi item manualmente.`
                : 'Aggiungi item manualmente o importa un template dalle impostazioni.'}
            </p>
          </div>
          {defaultTemplate && (
            <Button
              size="sm"
              onClick={() => applyMutation.mutate({ templateId: defaultTemplate.id, mode: 'replace' })}
              loading={applyMutation.isPending}
            >
              Applica template predefinito
            </Button>
          )}
        </div>
      )}

      {/* Add item */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1rem',
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'flex-end',
      }}>
        <div style={{ flex: 1 }}>
          <input
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            placeholder="Aggiungi un item alla checklist…"
            onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) addMutation.mutate({ label: newItem.trim(), category: newCategory }); }}
            style={{
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
              fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', width: '100%', outline: 'none',
            }}
          />
        </div>
        <select
          value={newCategory}
          onChange={e => setNewCategory(e.target.value)}
          style={{
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', color: 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', cursor: 'pointer', outline: 'none',
          }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button
          size="sm"
          onClick={() => { if (newItem.trim()) addMutation.mutate({ label: newItem.trim(), category: newCategory }); }}
          loading={addMutation.isPending}
          icon={<Plus size={14} />}
        >
          Aggiungi
        </Button>
      </div>

      {/* Categories */}
      {CATEGORIES.map(category => {
        const catItems = grouped[category] ?? [];
        const catDone = catItems.filter(i => i.completed).length;
        if (catItems.length === 0) return null;
        return (
          <div key={category} style={{
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '0.75rem 1rem',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--glass-bg-card)',
            }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{category}</span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {catDone}/{catItems.length}
              </span>
            </div>
            <div>
              {catItems.map(item => (
                <div key={item.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.625rem 1rem',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  transition: 'background var(--transition-interactive)',
                }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--glass-bg-card)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <button
                    onClick={() => toggleMutation.mutate({ itemId: item.id, completed: !item.completed })}
                    aria-label={item.completed ? 'Deseleziona' : 'Completa'}
                    style={{
                      width: 20, height: 20, borderRadius: 'var(--radius-interactive)', flexShrink: 0,
                      background: item.completed ? 'var(--color-primary)' : 'transparent',
                      border: `2px solid ${item.completed ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all var(--transition-interactive)',
                    }}
                  >
                    {item.completed && <Check size={11} color="white" />}
                  </button>
                  <span style={{
                    flex: 1, fontSize: 'var(--text-sm)',
                    textDecoration: item.completed ? 'line-through' : 'none',
                    color: item.completed ? 'var(--color-text-faint)' : 'var(--color-text)',
                  }}>
                    {item.label}
                  </span>
                  <button
                    onClick={() => deleteMutation.mutate(item.id)}
                    aria-label="Elimina item"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.125rem', display: 'flex', opacity: 0 }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Template modal */}
      <Modal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Applica template checklist"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowTemplateModal(false)}>Annulla</Button>
            <Button
              onClick={handleApply}
              loading={applyMutation.isPending}
              disabled={!selectedTemplateId && !defaultTemplate}
            >
              Applica
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {templates.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Nessun template disponibile per questo tipo di evento. Creane uno nelle Impostazioni → Checklist.
            </p>
          ) : (
            <>
              <div>
                <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, display: 'block', marginBottom: '0.375rem' }}>
                  Template
                </label>
                <select
                  value={selectedTemplateId}
                  onChange={e => setSelectedTemplateId(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
                    fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none',
                  }}
                >
                  <option value="">
                    {defaultTemplate ? `${defaultTemplate.name} (predefinito)` : 'Seleziona template'}
                  </option>
                  {templates.filter(t => !t.isDefault || selectedTemplateId).map(t => (
                    <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (predefinito)' : ''}</option>
                  ))}
                </select>
              </div>

              {(() => {
                const activeTemplate = templates.find(t => t.id === selectedTemplateId) ?? defaultTemplate;
                return activeTemplate ? (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                    {activeTemplate.items.length} item · {[...new Set(activeTemplate.items.map(i => i.category).filter(Boolean))].join(', ')}
                  </p>
                ) : null;
              })()}

              {items.length > 0 && (
                <div>
                  <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, display: 'block', marginBottom: '0.375rem' }}>
                    Modalità
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {(['append', 'replace'] as const).map(mode => (
                      <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
                        <input
                          type="radio"
                          name="applyMode"
                          value={mode}
                          checked={applyMode === mode}
                          onChange={() => setApplyMode(mode)}
                        />
                        {mode === 'append' ? 'Aggiungi agli item esistenti' : 'Sostituisci tutti gli item esistenti'}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
